import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { IColumnDefinitionView } from '../models/IColumnDefinitionView';
import { Lines } from '../models/line';
import { QuickSets } from '../models/QuickSets';
import { TblBidDataDisplay } from '../models/TblBidDataDisplay';
import { Trip } from '../models/Trip/Trip.model';
import { UserData } from '../models/UserData';
import { INI } from '../models/WbidINI';
import { StateFiles, WBidStateCollection } from '../models/WBidStateCollection';
import {
  AddColumnDefinition,
  RemoveColumnDefinition,
} from './actions/columnDefenition.action';
import {
  AddCurrentBidData,
  RemoveCurrentBidData,
} from './actions/CurrentBidData.action';
import { AddINI, RemoveINI } from './actions/ini.action';
import { AddLines, RemoveLines } from './actions/line.action';
import { AddQuickSet, RemoveQuickSet } from './actions/quickSet.action';
import { AddStateFile, RemoveStateFile } from './actions/stateFile.action';
import { AddTrips, RemoveTrips } from './actions/trips.action';
import { AddUserData, RemoveUserData } from './actions/userData.action';
import { DexieOperations } from './dexie-operation';
import { ColumnDefinitionState } from './states/columnDefenition.state';
import { CurrentBidDataState } from './states/currentBidData.state';
import { INIState } from './states/ini.state';
import { LinesState } from './states/line.state';
import { StateFileState } from './states/stateFile.state';
import { TripState } from './states/trip.state';
import { UserDataState } from './states/userData.state';

@Injectable()
export class GlobalStoreOperation {
  constructor(private store: Store, private dexie: DexieOperations) {}

  public getCurrentBidData = (callBack?): TblBidDataDisplay => {
    const currentBidData =
      this.store.selectSnapshot(CurrentBidDataState).currentBidData;
    if (currentBidData == undefined) {
      const empNum = localStorage.getItem('empnum').replace(/[^\d.]/g, '');
      const keyCombination = localStorage.getItem('bidId');
      this.dexie.getCurrentBidData(empNum, keyCombination, (res) => {
        this.removeAddCurrentBid(res, true);
        if (callBack)
          callBack(
            this.store.selectSnapshot(CurrentBidDataState).currentBidData
          );
        else
          return this.store.selectSnapshot(CurrentBidDataState).currentBidData;
      });
    } else {
      if (callBack)
        callBack(this.store.selectSnapshot(CurrentBidDataState).currentBidData);
      else return this.store.selectSnapshot(CurrentBidDataState).currentBidData;
    }
  };

  public getUserData = (): UserData => {
    return this.store.selectSnapshot(UserDataState).userData;
  };
  public getINIData = (callBack?): INI => {
    const currentBidData = this.store.selectSnapshot(INIState).ini;
    if (currentBidData == undefined) {
      const empNum = localStorage.getItem('empnum').replace(/[^\d.]/g, '');
      const keyCombination = localStorage.getItem('bidId');
      this.dexie.getCurrentBidData(empNum, keyCombination, (res) => {
        this.removeAddINI(res, true);
        if (callBack) callBack(this.store.selectSnapshot(INIState).ini);
        else return this.store.selectSnapshot(INIState).ini;
      });
    } else {
      if (callBack) callBack(this.store.selectSnapshot(INIState).ini);
      else return this.store.selectSnapshot(INIState).ini;
    }
    return this.store.selectSnapshot(INIState).ini;
  };
  public getLineData = (): Lines[] => {
    return this.store.selectSnapshot(LinesState).lines;
  };

  public getColDef = (): IColumnDefinitionView[] => {
    return this.store.selectSnapshot(ColumnDefinitionState).colDef;
  };

  public getTripData = (): Trip[] => {
    return this.store.selectSnapshot(TripState).trips;
  };
  public getSelectedColumnDefinition = (callBack?): IColumnDefinitionView[] => {
    const columnDef = this.store.selectSnapshot(ColumnDefinitionState).colDef;
    if (columnDef == undefined || columnDef.length == 0) {
      this.dexie.GetColumnDefinition((res) => {
        this.removeAddColumnDefinition(
          JSON.parse(res.columnDefinitionJsonString),
          true
        );
        if (callBack)
          callBack(this.store.selectSnapshot(ColumnDefinitionState).colDef);
        else return this.store.selectSnapshot(ColumnDefinitionState).colDef;
      });
    } else {
      if (callBack)
        callBack(this.store.selectSnapshot(ColumnDefinitionState).colDef);
      else return this.store.selectSnapshot(ColumnDefinitionState).colDef;
    }
  };
  public getStateFile = (): StateFiles => {
    return this.store.selectSnapshot(StateFileState).stateFile;
  };

  public removeAddUserData = (
    UserData?: UserData,
    isNeedInsert?: boolean
  ): void => {
    try {
      this.store.dispatch(RemoveUserData);
      if (isNeedInsert && UserData) {
        this.store.dispatch(new AddUserData(UserData));
      }
    } catch (ex) {
      throw `ClassName: GlobalStoreOperation, FunctionName: removeAddUserData, ErrorMessage: ${ex.stack.toString()}`;
    }
  };

  public removeAddColumnDefinition = (
    ColumnDefinition?: IColumnDefinitionView[],
    isNeedInsert?: boolean
  ): void => {
    try {
      this.store.dispatch(RemoveColumnDefinition);
      if (isNeedInsert && ColumnDefinition.length > 0) {
        this.store.dispatch(new AddColumnDefinition(ColumnDefinition));
      }
    } catch (ex) {
      throw `ClassName: GlobalStoreOperation, FunctionName: removeAddColumnDefinition, ErrorMessage: ${ex.stack.toString()}`;
    }
  };

  public removeAddLineData = (
    lineData?: Lines[],
    isNeedInsert?: boolean
  ): void => {
    try {
      this.store.dispatch(RemoveLines);
      if (isNeedInsert && lineData.length > 0) {
        this.store.dispatch(new AddLines(lineData));
      }
    } catch (ex) {
      throw `ClassName: GlobalStoreOperation, FunctionName: removeAddLineData, ErrorMessage: ${ex.stack.toString()}`;
    }
  };

  public removeAddINI = (iniData?: INI, isNeedInsert?: boolean): void => {
    try {
      this.store.dispatch(RemoveINI);
      if (isNeedInsert && iniData) {
        this.store.dispatch(new AddINI(iniData));
      }
    } catch (ex) {
      throw `ClassName: GlobalStoreOperation, FunctionName: removeAddINI, ErrorMessage: ${ex.stack.toString()}`;
    }
  };

  public removeAddTrips = (tripData?: Trip[], isNeedInsert?: boolean): void => {
    try {
      this.store.dispatch(RemoveTrips);
      if (isNeedInsert && tripData.length > 0) {
        this.store.dispatch(new AddTrips(tripData));
      }
    } catch (ex) {
      throw `ClassName: GlobalStoreOperation, FunctionName: removeAddTrips, ErrorMessage: ${ex.stack.toString()}`;
    }
  };

  public removeAddCurrentBid = (
    bidData?: TblBidDataDisplay,
    isNeedInsert?: boolean
  ): void => {
    try {
      this.store.dispatch(RemoveCurrentBidData);
      if (isNeedInsert && bidData) {
        this.store.dispatch(new AddCurrentBidData(bidData));
      }
    } catch (ex) {
      throw `ClassName: GlobalStoreOperation, FunctionName: removeAddCurrentBid, ErrorMessage: ${ex.stack.toString()}`;
    }
  };
  public removeAddStateFile = (
    stateData?: WBidStateCollection,
    isNeedInsert?: boolean
  ): void => {
    try {
      this.store.dispatch(RemoveStateFile);
      if (isNeedInsert && stateData) {
        this.store.dispatch(new AddStateFile(stateData));
      }
    } catch (ex) {
      throw `ClassName: GlobalStoreOperation, FunctionName: removeAddStateFile, ErrorMessage: ${ex.stack.toString()}`;
    }
  };

  public removeAddQuickSet = (
    quickSetData?: QuickSets,
    isNeedInsert?: boolean
  ): void => {
    try {
      this.store.dispatch(RemoveQuickSet);
      if (isNeedInsert && quickSetData) {
        this.store.dispatch(new AddQuickSet(quickSetData));
      }
    } catch (ex) {
      throw `ClassName: GlobalStoreOperation, FunctionName: removeAddQuickSet, ErrorMessage: ${ex.stack.toString()}`;
    }
  };
}
